import React, { useState } from 'react';
import { CalendarIcon, CheckCircleIcon, DownloadIcon, FileTextIcon, TruckIcon, ClockIcon, AlertTriangleIcon, InfoIcon, SearchIcon, FilterIcon } from 'lucide-react';
interface ApprovedRequest {
  id: string;
  truckingCompany: string;
  jobNumber: string;
  jobName: string;
  completionDate: string;
  requestDate: string;
  approvalDate: string;
  amount: number;
  invoiceNumber: string;
  status: string;
  priority: string;
  contractor: string;
  contractorRating: string;
  completedLoads: number;
  totalHours: number;
  documents: any[];
  notes: string;
  paymentTerms: string;
  factorRate: number;
  achFileName?: string;
  achGenerationDate?: string;
  expectedPaymentDate?: string;
}
interface ACHGeneratorProps {
  requests: ApprovedRequest[];
}
export const ACHGenerator: React.FC<ACHGeneratorProps> = ({
  requests
}) => {
  const [selectedRequests, setSelectedRequests] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [generationDate, setGenerationDate] = useState(new Date().toISOString().split('T')[0]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [generatedFileName, setGeneratedFileName] = useState('');
  // Filter requests that haven't had ACH files generated yet
  const pendingACHRequests = requests.filter(req => !req.achFileName || !req.achGenerationDate);
  // Filter requests based on search term
  const filteredRequests = pendingACHRequests.filter(request => searchTerm === '' || request.truckingCompany.toLowerCase().includes(searchTerm.toLowerCase()) || request.jobNumber.toLowerCase().includes(searchTerm.toLowerCase()) || request.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()));
  const handleSelectAll = () => {
    if (selectedRequests.length === filteredRequests.length) {
      setSelectedRequests([]);
    } else {
      setSelectedRequests(filteredRequests.map(req => req.id));
    }
  };
  const handleSelectRequest = (requestId: string) => {
    if (selectedRequests.includes(requestId)) {
      setSelectedRequests(selectedRequests.filter(id => id !== requestId));
    } else {
      setSelectedRequests([...selectedRequests, requestId]);
    }
  };
  const handleGenerateACHFile = () => {
    const fileName = `ACH_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}_${selectedRequests.length}_requests.ach`;
    setGeneratedFileName(fileName);
    setShowConfirmation(true);
  };
  const handleConfirmGeneration = () => {
    // In a real application, this would generate the actual ACH file
    alert(`ACH file ${generatedFileName} generated for ${selectedRequests.length} requests`);
    setShowConfirmation(false);
    setSelectedRequests([]);
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    });
  };
  const getTotalAmount = () => {
    return filteredRequests.filter(req => selectedRequests.includes(req.id)).reduce((total, req) => total + req.amount, 0);
  };
  const getNetAmount = () => {
    return filteredRequests.filter(req => selectedRequests.includes(req.id)).reduce((total, req) => total + (req.amount - req.amount * req.factorRate / 100), 0);
  };
  return <div className="bg-white rounded-lg shadow" data-id="element-2837">
      <div className="p-4 border-b" data-id="element-2838">
        <h2 className="text-lg font-medium flex items-center gap-2" data-id="element-2839">
          <div size={20} className="text-[#0F2A52]" data-id="element-2840" />
          ACH File Generator
        </h2>
      </div>
      <div className="p-4 border-b bg-gray-50" data-id="element-2841">
        <div className="flex flex-wrap gap-4 items-center" data-id="element-2842">
          <div className="flex-1 min-w-[200px]" data-id="element-2843">
            <div className="relative" data-id="element-2844">
              <input type="text" placeholder="Search by company or invoice..." className="w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F2A52] focus:border-transparent" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-2845" />
              <SearchIcon size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-2846" />
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-2847">
            <CalendarIcon size={18} className="text-gray-500" data-id="element-2848" />
            <input type="date" value={generationDate} onChange={e => setGenerationDate(e.target.value)} className="border rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-[#0F2A52] focus:border-transparent" data-id="element-2849" />
          </div>
          <button onClick={handleGenerateACHFile} disabled={selectedRequests.length === 0} className={`bg-[#0F2A52] text-white px-4 py-2 rounded-md flex items-center gap-2 ${selectedRequests.length === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#0F2A52]/90'}`} data-id="element-2850">
            <div size={18} data-id="element-2851" />
            Generate ACH File
          </button>
        </div>
      </div>
      {filteredRequests.length === 0 ? <div className="p-6 text-center" data-id="element-2852">
          <div className="bg-blue-50 p-4 rounded-lg inline-flex flex-col items-center" data-id="element-2853">
            <InfoIcon size={36} className="text-blue-500 mb-2" data-id="element-2854" />
            <p className="text-blue-800 font-medium" data-id="element-2855">No pending ACH requests</p>
            <p className="text-blue-600 text-sm mt-1" data-id="element-2856">
              All approved requests have been processed for payment
            </p>
          </div>
        </div> : <>
          <div className="p-4 bg-blue-50" data-id="element-2857">
            <div className="flex items-start gap-3" data-id="element-2858">
              <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-1" data-id="element-2859" />
              <div data-id="element-2860">
                <p className="text-blue-800 font-medium" data-id="element-2861">
                  About ACH File Generation
                </p>
                <p className="text-blue-700 text-sm mt-1" data-id="element-2862">
                  ACH (Automated Clearing House) files are used for electronic
                  funds transfers between banks. Select the approved requests
                  you want to include in your ACH file, then click "Generate ACH
                  File" to create a file for your banking system.
                </p>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto" data-id="element-2863">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-2864">
              <thead className="bg-gray-50" data-id="element-2865">
                <tr data-id="element-2866">
                  <th scope="col" className="px-6 py-3 text-left" data-id="element-2867">
                    <div className="flex items-center" data-id="element-2868">
                      <input type="checkbox" checked={selectedRequests.length === filteredRequests.length && filteredRequests.length > 0} onChange={handleSelectAll} className="h-4 w-4 text-[#0F2A52] focus:ring-[#0F2A52] border-gray-300 rounded" data-id="element-2869" />
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2870">
                    Company
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2871">
                    Invoice Details
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2872">
                    Approval Date
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2873">
                    Amount
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2874">
                    Net Payment
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-2875">
                {filteredRequests.map(request => <tr key={request.id} className={`hover:bg-gray-50 ${selectedRequests.includes(request.id) ? 'bg-blue-50' : ''}`} data-id="element-2876">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2877">
                      <div className="flex items-center" data-id="element-2878">
                        <input type="checkbox" checked={selectedRequests.includes(request.id)} onChange={() => handleSelectRequest(request.id)} className="h-4 w-4 text-[#0F2A52] focus:ring-[#0F2A52] border-gray-300 rounded" data-id="element-2879" />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2880">
                      <div className="flex items-center" data-id="element-2881">
                        <div className="flex-shrink-0 h-10 w-10 bg-[#0F2A52] rounded-full flex items-center justify-center text-white" data-id="element-2882">
                          <TruckIcon size={20} data-id="element-2883" />
                        </div>
                        <div className="ml-4" data-id="element-2884">
                          <div className="text-sm font-medium text-gray-900" data-id="element-2885">
                            {request.truckingCompany}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-2886">
                            {request.contractor}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2887">
                      <div className="text-sm text-gray-900" data-id="element-2888">
                        {request.invoiceNumber}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-2889">
                        {request.jobNumber}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2890">
                      <div className="text-sm text-gray-900 flex items-center gap-1" data-id="element-2891">
                        <CheckCircleIcon size={14} className="text-green-500" data-id="element-2892" />
                        {formatDate(request.approvalDate)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2893">
                      <div className="text-sm font-medium text-gray-900" data-id="element-2894">
                        {formatCurrency(request.amount)}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-2895">
                        {request.factorRate}% factor rate
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-2896">
                      <div className="text-sm font-medium text-green-600" data-id="element-2897">
                        {formatCurrency(request.amount - request.amount * request.factorRate / 100)}
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          {/* Summary Footer */}
          <div className="p-4 border-t bg-gray-50" data-id="element-2898">
            <div className="flex flex-wrap justify-between items-center" data-id="element-2899">
              <div data-id="element-2900">
                <p className="text-sm text-gray-600" data-id="element-2901">
                  {selectedRequests.length} of {filteredRequests.length}{' '}
                  requests selected
                </p>
              </div>
              <div className="flex gap-6" data-id="element-2902">
                <div className="text-right" data-id="element-2903">
                  <p className="text-sm text-gray-600" data-id="element-2904">Total Amount</p>
                  <p className="text-lg font-medium text-[#0F2A52]" data-id="element-2905">
                    {formatCurrency(getTotalAmount())}
                  </p>
                </div>
                <div className="text-right" data-id="element-2906">
                  <p className="text-sm text-gray-600" data-id="element-2907">Net Payment</p>
                  <p className="text-lg font-medium text-green-600" data-id="element-2908">
                    {formatCurrency(getNetAmount())}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>}
      {/* Confirmation Modal */}
      {showConfirmation && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-2909">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full" data-id="element-2910">
            <h3 className="text-lg font-medium mb-4 flex items-center gap-2" data-id="element-2911">
              <div size={20} className="text-[#0F2A52]" data-id="element-2912" />
              Confirm ACH File Generation
            </h3>
            <div className="mb-4 p-4 bg-blue-50 rounded-lg" data-id="element-2913">
              <p className="text-blue-800 font-medium mb-2" data-id="element-2914">File Details</p>
              <p className="text-blue-700 text-sm mb-2" data-id="element-2915">
                <span className="font-medium" data-id="element-2916">Filename:</span>{' '}
                {generatedFileName}
              </p>
              <p className="text-blue-700 text-sm mb-2" data-id="element-2917">
                <span className="font-medium" data-id="element-2918">Requests:</span>{' '}
                {selectedRequests.length}
              </p>
              <p className="text-blue-700 text-sm mb-2" data-id="element-2919">
                <span className="font-medium" data-id="element-2920">Total Amount:</span>{' '}
                {formatCurrency(getTotalAmount())}
              </p>
              <p className="text-blue-700 text-sm" data-id="element-2921">
                <span className="font-medium" data-id="element-2922">Net Payment Amount:</span>{' '}
                {formatCurrency(getNetAmount())}
              </p>
            </div>
            <p className="mb-4 text-gray-700" data-id="element-2923">
              This will generate an ACH file for the selected requests. The file
              will be ready for download and can be uploaded to your banking
              system for processing.
            </p>
            <div className="flex justify-end gap-3" data-id="element-2924">
              <button onClick={() => setShowConfirmation(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-2925">
                Cancel
              </button>
              <button onClick={handleConfirmGeneration} className="px-4 py-2 bg-[#0F2A52] hover:bg-[#0F2A52]/90 text-white rounded-md flex items-center gap-2" data-id="element-2926">
                <div size={18} data-id="element-2927" />
                Generate File
              </button>
            </div>
          </div>
        </div>}
    </div>;
};